import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Alert,
  Divider,
  LinearProgress
} from '@mui/material';
import {
  CheckCircle,
  TrendingUp,
  Security,
  AccountBalance,
  People,
  Code,
  Gavel,
  Timeline,
  Speed,
  Lock,
  Public,
  LocalAtm,
  EmojiObjects,
  Warning,
  Home,
  Token,
  HowToVote,
  School,
  Info,
  Storage,
  CloudQueue,
  Language,
  AttachMoney,
  VerifiedUser,
  AccountTree,
  Apartment
} from '@mui/icons-material';

const NFTCooperativasPresentation = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [tabValue, setTabValue] = useState(0);

  const sections = [
    'Introducción',
    'Aplicaciones',
    'Inversión',
    'Casos Reales',
    'Tecnología',
    'Implementación',
    'Legal',
    'Plan Acción',
    'Presupuesto',
    'Recursos'
  ];

  const handleNext = () => setActiveStep((prev) => Math.min(prev + 1, sections.length - 1));
  const handleBack = () => setActiveStep((prev) => Math.max(prev - 1, 0));
  const handleStepClick = (step) => setActiveStep(step);

  // SECCIÓN 0: INTRODUCCIÓN
  const Introduccion = () => (
    <Box>
      <Typography variant="h3" gutterBottom color="primary" sx={{ mb: 4, fontWeight: 'bold' }}>
        ¿Qué son los NFTs?
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card elevation={3} sx={{ height: '100%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent>
              <Token sx={{ fontSize: 60, mb: 2 }} />
              <Typography variant="h5" gutterBottom fontWeight="bold">
                Non-Fungible Tokens
              </Typography>
              <Typography variant="body1">
                Activos digitales únicos que representan la propiedad o autenticidad de un elemento específico en una blockchain.
              </Typography>
              <Typography variant="body2" sx={{ mt: 2, opacity: 0.9 }}>
                A diferencia de Bitcoin o Ethereum (fungibles), cada NFT es único e irrepetible.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={3} sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                Características Principales
              </Typography>
              <List>
                {[
                  { title: 'Unicidad', desc: 'Identificador único que lo distingue' },
                  { title: 'Indivisibilidad', desc: 'No se dividen en partes' },
                  { title: 'Verificable', desc: 'Blockchain registra al propietario' },
                  { title: 'Transferible', desc: 'Comprar, vender o transferir' },
                  { title: 'Programable', desc: 'Contratos inteligentes integrados' }
                ].map((item, i) => (
                  <ListItem key={i}>
                    <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                    <ListItemText 
                      primary={item.title}
                      secondary={item.desc}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                Cómo Funcionan Técnicamente
              </Typography>
              <Typography variant="body1" paragraph>
                Los NFTs se crean mediante "minting" (acuñación) en blockchains como Ethereum, Polygon, Solana.
              </Typography>
              <Grid container spacing={2}>
                {[
                  { icon: <Code />, title: 'Metadatos', desc: 'Describen el activo' },
                  { icon: <Storage />, title: 'Enlace digital', desc: 'Archivo (imagen, video, audio)' },
                  { icon: <VerifiedUser />, title: 'Información', desc: 'Creador y propietario' },
                  { icon: <AccountTree />, title: 'Smart Contracts', desc: 'Reglas y regalías' }
                ].map((item, i) => (
                  <Grid item xs={12} md={3} key={i}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                      <Box sx={{ color: 'primary.main', fontSize: 40, mb: 1 }}>{item.icon}</Box>
                      <Typography variant="subtitle2" fontWeight="bold">{item.title}</Typography>
                      <Typography variant="caption" color="text.secondary">{item.desc}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                Fungible vs No Fungible
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Fungible (Bitcoin, USD)</strong></TableCell>
                      <TableCell><strong>No Fungible (NFT, Propiedad)</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>Intercambiable por otro idéntico</TableCell>
                      <TableCell>Único e irremplazable</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Divisible</TableCell>
                      <TableCell>Indivisible</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Valor uniforme</TableCell>
                      <TableCell>Valor único por características</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>1 BTC = 1 BTC</TableCell>
                      <TableCell>Cada NFT es diferente</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  // SECCIÓN 5: IMPLEMENTACIÓN
  const Implementacion = () => (
    <Box>
      <Typography variant="h3" gutterBottom color="primary" sx={{ mb: 4, fontWeight: 'bold' }}>
        Implementación Práctica con Hyperledger
      </Typography>

      <Grid container spacing={3}>
        {/* ARQUITECTURA */}
        <Grid item xs={12}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
                Arquitectura Hyperledger Fabric
              </Typography>
              <Box sx={{ bgcolor: 'grey.900', color: 'white', p: 3, borderRadius: 2, fontFamily: 'monospace', fontSize: '0.85rem', overflowX: 'auto' }}>
                <pre style={{ margin: 0 }}>{`
┌─────────────────────────────────────────┐
│   APLICACIÓN WEB/MÓVIL COOPERATIVA      │
│  (Interfaz para miembros y admins)      │
└──────────────┬──────────────────────────┘
              │
    ┌─────────▼─────────┐
    │   APIs / SDK      │
    └─────────┬─────────┘
              │
┌─────────────▼────────────────────────────┐
│  HYPERLEDGER FABRIC NETWORK            │
│  • Peer Nodes (Miembros, Admin)        │
│  • Smart Contracts (Chaincode)         │
│  • Ordering Service                    │
│  • Certificate Authority               │
└─────────────────────────────────────────┘
                `}</pre>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* CRONOGRAMA DETALLADO */}
        <Grid item xs={12}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
                Cronograma de Implementación
              </Typography>
              <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tab label="Año 1" />
                <Tab label="Año 2" />
                <Tab label="Año 3+" />
              </Tabs>

              {tabValue === 0 && (
                <Grid container spacing={2}>
                  {[
                    { q: 'Q1', title: 'Educación y Planificación', items: ['Talleres stakeholders', 'Consultas legales', 'Diseño arquitectura'] },
                    { q: 'Q2', title: 'Desarrollo Piloto', items: ['Setup infraestructura', 'Smart contracts básicos', 'Interfaz simple'] },
                    { q: 'Q3', title: 'Testing Piloto', items: ['30 usuarios piloto', '1 propiedad tokenizada', 'Recolección feedback'] },
                    { q: 'Q4', title: 'Evaluación', items: ['Análisis resultados', 'GO/NO-GO', 'Planificación Año 2'] }
                  ].map((q, i) => (
                    <Grid item xs={12} md={6} key={i}>
                      <Card variant="outlined" sx={{ bgcolor: 'grey.50', height: '100%' }}>
                        <CardContent>
                          <Chip label={q.q} color="primary" sx={{ mb: 1 }} />
                          <Typography variant="h6" fontWeight="bold">{q.title}</Typography>
                          <List dense>
                            {q.items.map((item, j) => (
                              <ListItem key={j}>
                                <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                                <ListItemText primary={item} />
                              </ListItem>
                            ))}
                          </List>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}

              {tabValue === 1 && (
                <Grid container spacing={2}>
                  {[
                    { q: 'Q1', title: 'Desarrollo Completo', items: ['Infraestructura escalable', 'Todos smart contracts', 'App completa'] },
                    { q: 'Q2', title: 'Testing Riguroso', items: ['Auditorías seguridad', 'Testing carga', 'Beta 100 usuarios'] },
                    { q: 'Q3', title: 'Lanzamiento', items: ['Soft launch', 'General launch', 'Onboarding masivo'] },
                    { q: 'Q4', title: 'Estabilización', items: ['Optimizaciones', 'Nuevas features', 'Documentación'] }
                  ].map((q, i) => (
                    <Grid item xs={12} md={6} key={i}>
                      <Card variant="outlined" sx={{ bgcolor: 'grey.50', height: '100%' }}>
                        <CardContent>
                          <Chip label={q.q} color="secondary" sx={{ mb: 1 }} />
                          <Typography variant="h6" fontWeight="bold">{q.title}</Typography>
                          <List dense>
                            {q.items.map((item, j) => (
                              <ListItem key={j}>
                                <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                                <ListItemText primary={item} />
                              </ListItem>
                            ))}
                          </List>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}

              {tabValue === 2 && (
                <Card variant="outlined" sx={{ bgcolor: 'grey.50', p: 3 }}>
                  <Typography variant="h6" fontWeight="bold">Operación Continua</Typography>
                  <List>
                    {['Operación estable', 'Nuevas integraciones', 'Escalamiento', 'Intercooperación', 'Modelo replicable'].map((item, i) => (
                      <ListItem key={i}>
                        <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </Card>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* INFRAESTRUCTURA */}
        <Grid item xs={12}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">Infraestructura Técnica</Typography>
              <Grid container spacing={2}>
                {[
                  { icon: <Code />, title: 'Hardware', specs: '4+ cores CPU, 8-16GB RAM, 50GB+ SSD' },
                  { icon: <Speed />, title: 'Rendimiento', specs: '3,500+ TPS, 99.5% Uptime, <2s Response' },
                  { icon: <LocalAtm />, title: 'Costos Cloud', specs: '$300-600/mes básico, $800-1,500/mes HA' }
                ].map((spec, i) => (
                  <Grid item xs={12} md={4} key={i}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                      <Box sx={{ fontSize: 50, color: '#2196f3', mb: 1 }}>{spec.icon}</Box>
                      <Typography variant="h6" fontWeight="bold">{spec.title}</Typography>
                      <Typography variant="body2">{spec.specs}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* EQUIPO NECESARIO */}
        <Grid item xs={12}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">Equipo Necesario</Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Rol</strong></TableCell>
                      <TableCell><strong>Cantidad</strong></TableCell>
                      <TableCell><strong>Responsabilidades</strong></TableCell>
                      <TableCell align="right"><strong>Salario Anual</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      ['Arquitecto Blockchain', '1', 'Diseño arquitectura, supervisión', '$80K-150K'],
                      ['Desarrolladores Blockchain', '2-3', 'Smart contracts, integración', '$60K-120K c/u'],
                      ['Desarrolladores Full-Stack', '2', 'Frontend, backend, APIs', '$50K-100K c/u'],
                      ['DevOps Engineer', '1', 'Infraestructura, deployment', '$60K-110K'],
                      ['QA/Testing', '1', 'Testing calidad, auditoría', '$40K-80K']
                    ].map((row, i) => (
                      <TableRow key={i}>
                        <TableCell><strong>{row[0]}</strong></TableCell>
                        <TableCell>{row[1]}</TableCell>
                        <TableCell>{row[2]}</TableCell>
                        <TableCell align="right"><Chip label={row[3]} size="small" color="primary" /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* TIEMPO TOTAL */}
        <Grid item xs={12}>
          <Alert severity="info" icon={<Timeline />}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Tiempo Total: 6-12 meses</Typography>
            <List dense>
              {[
                'Setup inicial de red: 1-2 semanas',
                'Desarrollo smart contracts: 2-4 meses',
                'Integración sistemas: 2-3 meses',
                'Testing y auditoría: 1-2 meses'
              ].map((item, i) => (
                <ListItem key={i}><ListItemText primary={`• ${item}`} /></ListItem>
              ))}
            </List>
          </Alert>
        </Grid>
      </Grid>
    </Box>
  );

  // SECCIÓN 6: LEGAL
  const Legal = () => (
    <Box>
      <Typography variant="h3" gutterBottom color="primary" sx={{ mb: 4, fontWeight: 'bold' }}>
        Marco Legal y Regulatorio
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Alert severity="warning" icon={<Warning />}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              ⚠️ Consulta Legal Obligatoria
            </Typography>
            <Typography>
              La tokenización de propiedades requiere asesoría legal especializada. Las leyes varían significativamente entre jurisdicciones.
            </Typography>
          </Alert>
        </Grid>

        {[
          { 
            title: 'Estados Unidos', 
            icon: <Gavel />, 
            color: '#f44336', 
            items: [
              'NFTs NO reconocidos como títulos legales',
              'Estructura necesaria: LLC + NFT',
              'SEC evalúa si tokens son valores',
              'Regulación estado por estado (fragmentada)'
            ]
          },
          { 
            title: 'Europa', 
            icon: <Public />, 
            color: '#2196f3', 
            items: [
              'Mayor apertura regulatoria',
              'MiCA Regulation estableciendo estándares',
              'Líderes: Estonia, Suiza, Malta',
              'Pilotos gubernamentales en varios países'
            ]
          },
          { 
            title: 'Dubai y EAU', 
            icon: <AccountBalance />, 
            color: '#ff9800', 
            items: [
              'VARA (Autoridad Virtual de Activos)',
              'Licencia VARA obligatoria para emisión comercial',
              'Marco regulatorio avanzado',
              'Cumplimiento como requisito base'
            ]
          }
        ].map((region, i) => (
          <Grid item xs={12} md={4} key={i}>
            <Card elevation={3} sx={{ height: '100%', borderTop: `5px solid ${region.color}` }}>
              <CardContent>
                <Box sx={{ fontSize: 50, color: region.color, mb: 2 }}>{region.icon}</Box>
                <Typography variant="h6" fontWeight="bold" color={region.color} gutterBottom>
                  {region.title}
                </Typography>
                <List dense>
                  {region.items.map((item, j) => (
                    <ListItem key={j}>
                      <ListItemIcon><CheckCircle sx={{ color: region.color }} fontSize="small" /></ListItemIcon>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  // SECCIÓN 7: PLAN DE ACCIÓN
  const PlanAccion = () => (
    <Box>
      <Typography variant="h3" gutterBottom color="primary" sx={{ mb: 4, fontWeight: 'bold' }}>
        Plan de Acción para Cooperativas
      </Typography>

      <Grid container spacing={3}>
        {/* PASOS INMEDIATOS */}
        <Grid item xs={12}>
          <Card elevation={3} sx={{ bgcolor: '#e8f5e9' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom color="success.main" fontWeight="bold">
                Pasos Inmediatos - Comenzar HOY
              </Typography>
              <Grid container spacing={2}>
                {[
                  { week: 'Semana 1', title: 'Educación Básica', tasks: ['Leer documentación', 'Ver videos blockchain', 'Compartir con junta', 'Agendar reunión'] },
                  { week: 'Semana 2-3', title: 'Exploración', tasks: ['Casos similares', 'Contactar cooperativas', 'Identificar consultores', 'Lista preguntas'] },
                  { week: 'Semana 4', title: 'Primera Consulta', tasks: ['Consultor blockchain', 'Abogado', 'Estimar costos', 'Evaluar viabilidad'] },
                  { week: 'Mes 2', title: 'Decisión', tasks: ['Presentar asamblea', 'Votación', 'Asignar presupuesto', 'Formar comité'] }
                ].map((phase, i) => (
                  <Grid item xs={12} md={6} key={i}>
                    <Card variant="outlined">
                      <CardContent>
                        <Chip label={phase.week} color="success" sx={{ mb: 2 }} />
                        <Typography variant="h6" fontWeight="bold">{phase.title}</Typography>
                        <List dense>
                          {phase.tasks.map((task, j) => (
                            <ListItem key={j}>
                              <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                              <ListItemText primary={task} />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* CASOS DE USO PRIORIZADOS */}
        <Grid item xs={12}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
                Casos de Uso Priorizados
              </Typography>
              <Grid container spacing={2}>
                {[
                  { p: '1', title: 'Tokenización Propiedades', color: '#f44336', roi: 'Alto', time: '6-12m', impact: ['Capital inmediato', 'Transacción -80%', 'Costos -50-70%'] },
                  { p: '2', title: 'Votación Digital', color: '#ff9800', roi: 'Medio', time: '3-6m', impact: ['Participación +40-60%', 'Costos -70%', 'Más rápido'] },
                  { p: '3', title: 'Gestión Membresías', color: '#ffc107', roi: 'Medio', time: '2-4m', impact: ['Admin simple', 'Sin fraude', 'Fácil transferir'] },
                  { p: '4', title: 'Auto Dividendos', color: '#4caf50', roi: 'Alto', time: '2-3m', impact: ['Ahorro 90%', 'Sin errores', 'Más frecuente'] }
                ].map((uc, i) => (
                  <Grid item xs={12} md={6} key={i}>
                    <Card variant="outlined" sx={{ borderLeft: `5px solid ${uc.color}`, height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Chip label={`Prioridad ${uc.p}`} sx={{ bgcolor: uc.color, color: 'white', mr: 2 }} />
                          <Typography variant="h6" fontWeight="bold">{uc.title}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                          <Chip label={`ROI: ${uc.roi}`} size="small" />
                          <Chip label={uc.time} size="small" />
                        </Box>
                        <Typography variant="subtitle2" fontWeight="bold">Impacto:</Typography>
                        <List dense>
                          {uc.impact.map((item, j) => (
                            <ListItem key={j}>
                              <ListItemIcon><TrendingUp color="success" fontSize="small" /></ListItemIcon>
                              <ListItemText primary={item} />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* KPIS */}
        <Grid item xs={12}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
                Métricas de Éxito (KPIs)
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Categoría</strong></TableCell>
                      <TableCell><strong>Métrica</strong></TableCell>
                      <TableCell align="right"><strong>Objetivo</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      ['Técnicos', 'Disponibilidad Sistema', '>99.5%'],
                      ['Técnicos', 'Tiempo Respuesta', '<2 seg'],
                      ['Técnicos', 'Transacciones/seg', '100+ TPS'],
                      ['Adopción', 'Tasa Adopción Mensual', '>5%'],
                      ['Adopción', 'Usuarios Activos', '>60%'],
                      ['Adopción', 'NPS', '>50'],
                      ['Financieros', 'Reducción Costos', '>30%'],
                      ['Financieros', 'ROI Proyecto', '18-24 meses'],
                      ['Gobernanza', 'Participación Votos', '>50%'],
                      ['Gobernanza', 'Tiempo Resolución', '-70%']
                    ].map((row, i) => (
                      <TableRow key={i}>
                        <TableCell><strong>{row[0]}</strong></TableCell>
                        <TableCell>{row[1]}</TableCell>
                        <TableCell align="right"><Chip label={row[2]} size="small" color="primary" /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={3} sx={{ bgcolor: '#e8f5e9' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom color="success.main" fontWeight="bold">
                Primeros Pasos para Implementar
              </Typography>
              <List>
                {[
                  '1. Educación: Capacitar a los miembros sobre blockchain y NFTs',
                  '2. Definir objetivos: Identificar qué problema específico resuelven',
                  '3. Elegir blockchain: Considerar costos, velocidad y sostenibilidad',
                  '4. Desarrollar estrategia: Plan de implementación gradual',
                  '5. Crear comunidad: Involucrar stakeholders desde el inicio',
                  '6. Pilotos pequeños: Empezar con proyectos limitados',
                  '7. Alianzas: Buscar asesores técnicos especializados'
                ].map((paso, i) => (
                  <ListItem key={i}>
                    <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                    <ListItemText primary={paso} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  // SECCIÓN 8: PRESUPUESTO
  const Presupuesto = () => (
    <Box>
      <Typography variant="h3" gutterBottom color="primary" sx={{ mb: 4, fontWeight: 'bold' }}>
        Presupuesto de Implementación
      </Typography>
      <Grid container spacing={3}>
        {[
          { 
            fase: 'Educación y Planificación', 
            color: '#2196f3', 
            items: [['Consultorías', '$15K-30K'], ['Legal', '$10K-25K']], 
            subtotal: '$30K-68K', 
            tiempo: '2-3 meses' 
          },
          { 
            fase: 'Proyecto Piloto', 
            color: '#ff9800', 
            items: [['Desarrollo', '$40K-75K'], ['Infraestructura', '$1.5K-3K']], 
            subtotal: '$60K-112K', 
            tiempo: '3-4 meses' 
          },
          { 
            fase: 'Implementación Completa', 
            color: '#4caf50', 
            items: [['Desarrollo full', '$120K-240K'], ['Infraestructura', '$18K-48K']], 
            subtotal: '$235K-504K', 
            tiempo: '6-8 meses' 
          }
        ].map((fase, i) => (
          <Grid item xs={12} key={i}>
            <Card elevation={3}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h5" fontWeight="bold" sx={{ color: fase.color }}>
                    FASE {i + 1}: {fase.fase}
                  </Typography>
                  <Chip label={fase.tiempo} sx={{ bgcolor: fase.color, color: 'white' }} />
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      {fase.items.map((item, j) => (
                        <TableRow key={j}>
                          <TableCell>{item[0]}</TableCell>
                          <TableCell align="right"><strong>{item[1]}</strong></TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell><strong>SUBTOTAL</strong></TableCell>
                        <TableCell align="right">
                          <Typography variant="h6" fontWeight="bold" sx={{ color: fase.color }}>
                            {fase.subtotal}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        ))}

        <Grid item xs={12}>
          <Card elevation={3} sx={{ bgcolor: 'primary.main', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h4" fontWeight="bold">INVERSIÓN TOTAL</Typography>
                <Typography variant="h3" fontWeight="bold">$325K - $684K</Typography>
              </Box>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Tiempo total: 12-18 meses | Costos anuales post-lanzamiento: $158K-315K
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  // SECCIÓN 9: RECURSOS
  const Recursos = () => (
    <Box>
      <Typography variant="h3" gutterBottom color="primary" sx={{ mb: 4, fontWeight: 'bold' }}>
        Recursos y Referencias
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardContent>
              <Code sx={{ fontSize: 50, color: '#2196f3', mb: 2 }} />
              <Typography variant="h5" gutterBottom fontWeight="bold" color="primary">
                Plataformas de Desarrollo
              </Typography>
              <List>
                {[
                  { name: 'Hyperledger Fabric', url: 'hyperledger.org' },
                  { name: 'Ethereum Docs', url: 'ethereum.org' },
                  { name: 'OpenZeppelin', url: 'openzeppelin.com' }
                ].map((resource, i) => (
                  <ListItem key={i}>
                    <ListItemIcon><Public color="primary" /></ListItemIcon>
                    <ListItemText primary={resource.name} secondary={resource.url} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardContent>
              <Home sx={{ fontSize: 50, color: '#4caf50', mb: 2 }} />
              <Typography variant="h5" gutterBottom fontWeight="bold" color="primary">
                Plataformas Real Estate NFT
              </Typography>
              <List>
                {[
                  { name: 'Propy', url: 'propy.com' },
                  { name: 'Lofty AI', url: 'lofty.ai' },
                  { name: 'RealT', url: 'realt.co' }
                ].map((resource, i) => (
                  <ListItem key={i}>
                    <ListItemIcon><Public color="success" /></ListItemIcon>
                    <ListItemText primary={resource.name} secondary={resource.url} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Alert severity="success" icon={<EmojiObjects />}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Conclusión Final
            </Typography>
            <Typography variant="body1">
              Los NFTs pueden ser una herramienta poderosa para cooperativas que buscan modernizarse, 
              aumentar transparencia y crear nuevos modelos de valor compartido. <strong>Hyperledger es 
              absolutamente viable y probablemente la mejor opción</strong> para cooperativas que priorizan 
              control, privacidad, costos bajos y cumplimiento regulatorio.
            </Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              La clave está en identificar casos de uso específicos que realmente beneficien a los miembros 
              y alinearse con los valores cooperativos de democracia, equidad y solidaridad.
            </Typography>
          </Alert>
        </Grid>
      </Grid>
    </Box>
  );

  const renderSection = () => {
    switch (activeStep) {
      case 0: return <Introduccion />;
      case 1: return <Aplicaciones />;
      case 2: return <Inversion />;
      case 3: return <CasosReales />;
      case 4: return <Tecnologia />;
      case 5: return <Implementacion />;
      case 6: return <Legal />;
      case 7: return <PlanAccion />;
      case 8: return <Presupuesto />;
      case 9: return <Recursos />;
      default: return <Introduccion />;
    }
  };

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography 
            variant="h2" 
            gutterBottom 
            sx={{ 
              fontWeight: 'bold',
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2
            }}
          >
            NFTs y Blockchain para Cooperativas
          </Typography>
          <Typography variant="h5" color="text.secondary" gutterBottom>
            Guía Completa de Implementación
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Transformando la vivienda cooperativa con tecnología blockchain
          </Typography>
        </Box>

        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {sections.map((label, index) => (
              <Step key={label}>
                <StepLabel 
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleStepClick(index)}
                >
                  <Typography variant="caption">{label}</Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        <Box sx={{ mb: 4 }}>
          {renderSection()}
        </Box>

        <Paper elevation={3} sx={{ p: 3, display: 'flex', justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={activeStep === 0}
            size="large"
          >
            Anterior
          </Button>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Sección {activeStep + 1} de {sections.length}
            </Typography>
          </Box>

          <Button
            variant="contained"
            onClick={handleNext}
            disabled={activeStep === sections.length - 1}
            size="large"
          >
            Siguiente
          </Button>
        </Paper>

        <Box sx={{ mt: 6, textAlign: 'center', py: 3 }}>
          <Typography variant="body2" color="text.secondary">
            © 2025 - Guía NFTs y Blockchain para Cooperativas
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Versión Completa - Documento vivo con información actualizada de casos reales
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default NFTCooperativasPresentation;

  // SECCIÓN 1: APLICACIONES
  const Aplicaciones = () => (
    <Box>
      <Typography variant="h3" gutterBottom color="primary" sx={{ mb: 4, fontWeight: 'bold' }}>
        Aplicaciones en Cooperativas
      </Typography>

      <Grid container spacing={3}>
        {[
          {
            icon: <HowToVote sx={{ fontSize: 50 }} />,
            title: 'Membresía y Gobernanza',
            color: '#3f51b5',
            items: [
              'Certificados de membresía digitales',
              'Derechos de voto tokenizados',
              'Historial de participación inmutable',
              'Transferencia facilitada de membresías'
            ]
          },
          {
            icon: <LocalAtm sx={{ fontSize: 50 }} />,
            title: 'Crowdfunding y Financiamiento',
            color: '#4caf50',
            items: [
              'Venta de NFTs para recaudar fondos',
              'NFTs con utilidad y acceso a servicios',
              'Bonos o certificados de inversión',
              'Recompensas permanentes en blockchain'
            ]
          },
          {
            icon: <Security sx={{ fontSize: 50 }} />,
            title: 'Certificación y Trazabilidad',
            color: '#ff9800',
            items: [
              'Certificados de autenticidad (orgánicos, comercio justo)',
              'Trazabilidad desde producción a consumidor',
              'Transparencia en cadena de suministro',
              'Combate a falsificación con pasaporte digital'
            ]
          },
          {
            icon: <Apartment sx={{ fontSize: 50 }} />,
            title: 'Propiedad Fraccionada',
            color: '#e91e63',
            items: [
              'División de activos costosos (inmuebles, terrenos)',
              'Inversión colectiva entre múltiples miembros',
              'Distribución automática de beneficios',
              'Acceso a activos antes inaccesibles'
            ]
          },
          {
            icon: <School sx={{ fontSize: 50 }} />,
            title: 'Educación y Capacitación',
            color: '#9c27b0',
            items: [
              'Certificados de cursos verificables',
              'Badges de habilidades específicas',
              'Portfolio profesional permanente',
              'Historial educativo inmutable'
            ]
          },
          {
            icon: <VerifiedUser sx={{ fontSize: 50 }} />,
            title: 'Reconocimiento y Reputación',
            color: '#00bcd4',
            items: [
              'Sistema de méritos por contribuciones',
              'Tokens por antigüedad y compromiso',
              'Conmemoración de hitos colectivos',
              'Incentivos para participación destacada'
            ]
          }
        ].map((app, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card 
              elevation={3} 
              sx={{ 
                height: '100%', 
                '&:hover': { transform: 'translateY(-8px)' }, 
                transition: '0.3s',
                borderTop: `4px solid ${app.color}`
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ color: app.color, mr: 2 }}>{app.icon}</Box>
                  <Typography variant="h5" fontWeight="bold" color={app.color}>
                    {app.title}
                  </Typography>
                </Box>
                <List dense>
                  {app.items.map((item, i) => (
                    <ListItem key={i}>
                      <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        ))}

        <Grid item xs={12}>
          <Alert severity="success" icon={<EmojiObjects />}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Ejemplo: Cooperativa Cafetera
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Membresía:</strong> Cada agricultor recibe un NFT con su nivel de participación
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Trazabilidad:</strong> Cada lote de café tiene un NFT que registra: agricultor, fecha de cosecha, 
              certificaciones, proceso de tostado y distribuidor
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Ventas directas:</strong> Consumidores compran café premium con NFT coleccionable que incluye historia 
              del productor, notas de cata y acceso a descuentos futuros
            </Typography>
            <Typography variant="body2">
              <strong>Regalías:</strong> Cada reventa del NFT genera un porcentaje para el agricultor original
            </Typography>
          </Alert>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                Plataformas Útiles para Cooperativas
              </Typography>
              <Grid container spacing={2}>
                {[
                  { name: 'OpenSea, Rarible', uso: 'Mercados generales de NFTs' },
                  { name: 'Mintbase, Zora', uso: 'Crear colecciones propias' },
                  { name: 'Arweave, IPFS', uso: 'Almacenamiento descentralizado' },
                  { name: 'Snapshot', uso: 'Votación con tokens' },
                  { name: 'Collab.Land', uso: 'Gestión de comunidades' }
                ].map((plat, i) => (
                  <Grid item xs={12} md={6} key={i}>
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                      <Typography variant="subtitle2" fontWeight="bold" color="primary">
                        {plat.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {plat.uso}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  // SECCIÓN 2: INVERSIÓN
  const Inversion = () => (
    <Box>
      <Typography variant="h3" gutterBottom color="primary" sx={{ mb: 4, fontWeight: 'bold' }}>
        Cómo Invertir en NFTs
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card elevation={3} sx={{ bgcolor: '#e3f2fd' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
                Paso a Paso para Invertir
              </Typography>
              
              <Grid container spacing={2}>
                {[
                  {
                    step: '1',
                    title: 'Preparación Inicial',
                    items: [
                      'Crear billetera digital (MetaMask, Phantom, Coinbase Wallet, Trust Wallet)',
                      'Comprar criptomonedas (ETH, SOL, MATIC)',
                      'Transferir a tu wallet personal'
                    ]
                  },
                  {
                    step: '2',
                    title: 'Elegir Marketplace',
                    items: [
                      'OpenSea - El más grande y variado',
                      'Blur - Para traders activos',
                      'Rarible - Enfocado en creadores',
                      'Foundation - Arte de alta calidad'
                    ]
                  },
                  {
                    step: '3',
                    title: 'Proceso de Compra',
                    items: [
                      'Conectar wallet al marketplace',
                      'Explorar y verificar autenticidad',
                      'Revisar historial de precios',
                      'Hacer oferta o comprar al precio listado'
                    ]
                  },
                  {
                    step: '4',
                    title: 'Tipos de Compra',
                    items: [
                      'Precio fijo - Compra inmediata',
                      'Subasta - Pujas contra otros',
                      'Ofertas - Proponer precio',
                      'Mint - Compra del creador directamente'
                    ]
                  }
                ].map((phase, i) => (
                  <Grid item xs={12} md={6} key={i}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Chip 
                            label={`Paso ${phase.step}`} 
                            color="primary" 
                            sx={{ mr: 2, fontWeight: 'bold' }}
                          />
                          <Typography variant="h6" fontWeight="bold">
                            {phase.title}
                          </Typography>
                        </Box>
                        <List dense>
                          {phase.items.map((item, j) => (
                            <ListItem key={j}>
                              <ListItemIcon><CheckCircle color="primary" fontSize="small" /></ListItemIcon>
                              <ListItemText primary={item} />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h4" gutterBottom color="primary" fontWeight="bold" sx={{ mt: 3 }}>
            Mercado Global de NFTs
          </Typography>
        </Grid>

        {[
          { icon: <TrendingUp />, value: '$40B+', label: 'Volumen pico (2022)', color: '#4caf50', desc: 'Mercado estabilizado con ciclos de actividad' },
          { icon: <People />, value: 'Millones', label: 'Usuarios globales', color: '#2196f3', desc: 'Participantes activos en todo el mundo' },
          { icon: <Language />, value: '180+', label: 'Países activos', color: '#ff9800', desc: 'USA, Europa, Asia y Latinoamérica' }
        ].map((stat, i) => (
          <Grid item xs={12} md={4} key={i}>
            <Card elevation={3} sx={{ textAlign: 'center', p: 3, height: '100%' }}>
              <Box sx={{ fontSize: 80, color: stat.color, mb: 2 }}>{stat.icon}</Box>
              <Typography variant="h3" fontWeight="bold" color="primary">{stat.value}</Typography>
              <Typography variant="h6" color="text.secondary" gutterBottom>{stat.label}</Typography>
              <Typography variant="body2" color="text.secondary">{stat.desc}</Typography>
            </Card>
          </Grid>
        ))}

        <Grid item xs={12}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
                Segmentos del Mercado
              </Typography>
              {[
                { name: 'Arte Digital', value: 35, color: '#e91e63', desc: 'Obras únicas, colecciones generativas, fotografía' },
                { name: 'Coleccionables/PFP', value: 30, color: '#9c27b0', desc: 'CryptoPunks, BAYC, avatares generativos' },
                { name: 'Gaming y Metaverso', value: 20, color: '#3f51b5', desc: 'Items de juegos, terrenos virtuales' },
                { name: 'Utilidad y Membresías', value: 10, color: '#00bcd4', desc: 'Acceso a comunidades, eventos, beneficios' },
                { name: 'Música y Otros', value: 5, color: '#4caf50', desc: 'Contenido multimedia, ediciones limitadas' }
              ].map((seg, i) => (
                <Box key={i} sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Box>
                      <Typography fontWeight="bold">{seg.name} - {seg.value}%</Typography>
                      <Typography variant="body2" color="text.secondary">{seg.desc}</Typography>
                    </Box>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={seg.value * 2} 
                    sx={{ 
                      height: 12, 
                      borderRadius: 5, 
                      '& .MuiLinearProgress-bar': { backgroundColor: seg.color }
                    }}
                  />
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
                ¿Por Qué Algunos NFTs Son Tan Costosos?
              </Typography>
              <Grid container spacing={2}>
                {[
                  { title: 'Escasez', desc: 'Ediciones limitadas. CryptoPunks: solo 10,000 existen', icon: <Token /> },
                  { title: 'Reputación', desc: 'Artistas reconocidos (Beeple: $69M)', icon: <VerifiedUser /> },
                  { title: 'Utilidad', desc: 'Acceso a comunidades, eventos, regalías', icon: <EmojiObjects /> },
                  { title: 'Importancia Histórica', desc: '"Primeros" de su categoría', icon: <Timeline /> },
                  { title: 'Comunidad', desc: 'Roadmap atractivo, marketing efectivo', icon: <People /> },
                  { title: 'Especulación', desc: 'FOMO, influencers, ciclos de hype', icon: <TrendingUp /> }
                ].map((factor, i) => (
                  <Grid item xs={12} md={4} key={i}>
                    <Card variant="outlined" sx={{ height: '100%', p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ color: 'primary.main', mr: 1, fontSize: 30 }}>{factor.icon}</Box>
                        <Typography variant="h6" fontWeight="bold">{factor.title}</Typography>
                      </Box>
                      <Typography variant="body2">{factor.desc}</Typography>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Ejemplos de Ventas Millonarias
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      {[
                        ['"Everydays: The First 5000 Days"', 'Beeple', '$69.3M'],
                        ['CryptoPunk #5822', 'Larva Labs', '$23.7M'],
                        ['"Clock"', 'Pak & Julian Assange', '$52.7M'],
                        ['Bored Ape #8817', 'Yuga Labs', '$3.4M']
                      ].map((row, i) => (
                        <TableRow key={i}>
                          <TableCell><strong>{row[0]}</strong></TableCell>
                          <TableCell>{row[1]}</TableCell>
                          <TableCell align="right">
                            <Chip label={row[2]} color="success" size="small" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Alert severity="warning" icon={<Warning />}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Riesgos de Invertir en NFTs
            </Typography>
            <Grid container spacing={2}>
              {[
                'Volatilidad Extrema (caídas del 90%+)',
                'Falta de Liquidez (difícil vender rápido)',
                'Fraudes y Estafas (rug pulls, hackeos)',
                'Problemas Técnicos (enlaces rotos, errores)',
                'Regulación Incierta (leyes no claras)',
                'Burbuja Especulativa (proyectos sin valor real)'
              ].map((risk, i) => (
                <Grid item xs={12} md={6} key={i}>
                  <Typography variant="body2">• {risk}</Typography>
                </Grid>
              ))}
            </Grid>
            <Typography variant="body2" sx={{ mt: 2, fontWeight: 'bold' }}>
              ⚠️ Solo invierte lo que puedas perder. Los NFTs son altamente especulativos.
            </Typography>
          </Alert>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={3} sx={{ bgcolor: '#e8f5e9' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom color="success.main" fontWeight="bold">
                Consejos para Invertir Inteligentemente
              </Typography>
              <Grid container spacing={2}>
                {[
                  {
                    title: 'DYOR (Do Your Own Research)',
                    tips: ['Estudiar el equipo del proyecto', 'Verificar roadmap y cumplimiento', 'Analizar la comunidad', 'Revisar código del contrato']
                  },
                  {
                    title: 'Diversificación',
                    tips: ['No todo en un proyecto', 'Mezclar categorías', 'Balance entre establecidos y nuevos', 'Gestión de riesgos']
                  },
                  {
                    title: 'Seguridad Primero',
                    tips: ['Hardware wallets para NFTs valiosos', 'Nunca compartir seed phrase', 'Verificar URLs', 'Desconfiar de ofertas perfectas']
                  },
                  {
                    title: 'Pensar a Largo Plazo',
                    tips: ['Evitar trading frenético', 'Buscar fundamentos sólidos', 'Considerar valor cultural', 'Paciencia y estrategia']
                  }
                ].map((consejo, i) => (
                  <Grid item xs={12} md={6} key={i}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" fontWeight="bold" color="success.main" gutterBottom>
                          {consejo.title}
                        </Typography>
                        <List dense>
                          {consejo.tips.map((tip, j) => (
                            <ListItem key={j}>
                              <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                              <ListItemText primary={tip} />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  // SECCIÓN 3: CASOS REALES
  const CasosReales = () => (
    <Box>
      <Typography variant="h3" gutterBottom color="primary" sx={{ mb: 4, fontWeight: 'bold' }}>
        Casos Reales de Vivienda NFT
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Alert severity="info">
            <Typography variant="body1">
              <strong>Modelos de Uso:</strong> Propiedad Fraccionada y Escrituras Tokenizadas son los principales modelos implementados en USA y Europa.
            </Typography>
          </Alert>
        </Grid>

        {[
          {
            title: 'Propy (Global)',
            subtitle: 'Primera venta de propiedad como NFT - Pionera',
            icon: <Home />,
            color: '#2196f3',
            items: [
              'Apartamento Michael Arrington (Kiev): $93,000 - "Primera NFT inmobiliaria del mundo"',
              'Casa en Tampa, Florida: $215,000',
              'Escrituras tokenizadas como NFTs para propiedad segura',
              'Transacciones internacionales facilitadas por blockchain',
              'Usa Hyperledger Fabric para documentos legales privados'
            ],
            stat: '10+ propiedades vendidas',
            details: 'Para vender como NFT, la propiedad se registra en una LLC. El comprador posee el NFT que otorga derechos sobre la LLC.'
          },
          {
            title: 'Lofty AI (USA)',
            subtitle: 'Propiedad fraccionada ultra-accesible',
            icon: <LocalAtm />,
            color: '#4caf50',
            items: [
              'Inversión desde $50 - La más accesible del mercado',
              'Pagos diarios en USD o criptomonedas',
              'Ingreso por alquiler proporcional a participación',
              'Mercado secundario activo para trading',
              'IA para identificar oportunidades de alto rendimiento',
              'Enfoque en propiedades residenciales USA'
            ],
            stat: 'Desde $50',
            details: 'Los inversores reciben rentas proporcionales y pueden vender tokens sin vender la propiedad completa.'
          },
          {
            title: 'RealT (USA)',
            subtitle: 'Especializada en ciudades específicas',
            icon: <Apartment />,
            color: '#ff5722',
            items: [
              'Propiedades en Detroit, Ohio y Florida',
              'Tokens basados en blockchain Ethereum',
              'Distribución automática de rentas semanales',
              'Comunidad activa de inversores',
              'Tokenización fraccionada de propiedades residenciales'
            ],
            stat: 'Rentas semanales',
            details: 'Modelo establecido con track record comprobado en mercado estadounidense.'
          },
          {
            title: 'The Shard (Londres)',
            subtitle: 'Tokenización de edificio icónico europeo',
            icon: <AccountBalance />,
            color: '#ff9800',
            items: [
              'NFTs fraccionarios del edificio The Shard',
              'Financiamiento completo logrado en DÍAS',
              'Dividendos para inversores según participación',
              'Trading en MERJ Exchange',
              'Cada NFT = porcentaje de participación en el activo'
            ],
            stat: 'Financiado en días',
            details: 'Ejemplo exitoso de tokenización de activo inmobiliario de alto valor en Europa.'
          },
          {
            title: 'Empowa (Mozambique)',
            subtitle: 'Vivienda asequible con impacto social',
            icon: <People />,
            color: '#9c27b0',
            items: [
              '30 familias alojadas - 50%+ hogares liderados por mujeres',
              'Modelo "lease-to-own" (alquiler con opción a compra)',
              'Unidades desde $10,000 con Casa Real',
              'Venta de NFTs genera liquidez para construcción',
              'Ingreso por alquiler redistribuido a holders de NFT',
              'Modelo replicable para cooperativas'
            ],
            stat: '30 familias',
            details: 'Piloto lanzado en sept 2022 por Mercy Corps Ventures. Modelo particularmente relevante para cooperativas de vivienda asequible.'
          },
          {
            title: 'Velox.RE (Reino Unido)',
            subtitle: 'Tokenización con cumplimiento regulatorio',
            icon: <Gavel />,
            color: '#00bcd4',
            items: [
              'Plataforma usando Hyperledger',
              'Enfoque en cumplimiento regulatorio británico',
              'Propiedad fraccionada con total privacidad',
              'Modelo diseñado para marco legal europeo'
            ],
            stat: 'UK compliant',
            details: 'Ejemplo de adaptación a regulaciones específicas europeas.'
          },
          {
            title: 'Harbor (USA)',
            subtitle: 'Tokenización de valores inmobiliarios',
            icon: <Security />,
            color: '#795548',
            items: [
              'Tokenización de valores inmobiliarios',
              'Hyperledger para gestión privada de inversores',
              'Cumplimiento automático con regulaciones SEC',
              'Enfoque en compliance y legalidad'
            ],
            stat: 'SEC compliant',
            details: 'Líder en cumplimiento regulatorio de tokenización inmobiliaria en USA.'
          }
        ].map((caso, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card 
              elevation={3}
              sx={{ 
                height: '100%',
                transition: 'transform 0.3s',
                '&:hover': { transform: 'scale(1.02)' },
                borderTop: `5px solid ${caso.color}`
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ color: caso.color, mr: 2, fontSize: 50 }}>{caso.icon}</Box>
                  <Box>
                    <Typography variant="h5" fontWeight="bold" color={caso.color}>
                      {caso.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {caso.subtitle}
                    </Typography>
                  </Box>
                </Box>
                
                <List dense>
                  {caso.items.map((item, i) => (
                    <ListItem key={i}>
                      <ListItemIcon>
                        <CheckCircle color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={item}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>

                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.100', borderRadius: 2, mb: 2 }}>
                  <Typography variant="h5" fontWeight="bold" color={caso.color}>
                    {caso.stat}
                  </Typography>
                </Box>

                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="caption">{caso.details}</Typography>
                </Alert>
              </CardContent>
            </Card>
          </Grid>
        ))}

        <Grid item xs={12}>
          <Card elevation={3} sx={{ bgcolor: '#fff3e0' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom fontWeight="bold" color="warning.main">
                Desafíos Legales Actuales
              </Typography>
              <Grid container spacing={2}>
                {[
                  {
                    title: 'Estados Unidos',
                    desc: 'Las oficinas de registro de tierras NO reconocen transferencias mediante NFTs. Necesario usar estructura LLC + NFT.',
                    severity: 'high'
                  },
                  {
                    title: 'Europa',
                    desc: 'Mayor claridad regulatoria. MiCA Regulation estableciendo estándares. Algunos países desarrollando marcos específicos.',
                    severity: 'medium'
                  },
                  {
                    title: 'Global',
                    desc: 'NFTs no son reconocidos como títulos de propiedad legales. Bienes raíces físicos no pueden tokenizarse directamente.',
                    severity: 'high'
                  },
                  {
                    title: 'Fiscal',
                    desc: 'Tratamiento impositivo ambiguo en muchos países. Necesario consultar con contador especializado.',
                    severity: 'medium'
                  }
                ].map((challenge, i) => (
                  <Grid item xs={12} md={6} key={i}>
                    <Alert 
                      severity={challenge.severity === 'high' ? 'warning' : 'info'}
                      icon={<Warning />}
                    >
                      <Typography variant="subtitle2" fontWeight="bold">
                        {challenge.title}
                      </Typography>
                      <Typography variant="body2">
                        {challenge.desc}
                      </Typography>
                    </Alert>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={3} sx={{ bgcolor: '#e8f5e9' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom fontWeight="bold" color="success.main">
                Ventajas para Proyectos de Vivienda
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
                    Financieras
                  </Typography>
                  <List dense>
                    {[
                      'Democratización del acceso (desde $50)',
                      'Liquidez mejorada (vender fracciones sin vender todo)',
                      'Acceso a capital global',
                      'Eliminación de intermediarios tradicionales',
                      'Velocidad: transacciones rápidas en blockchain'
                    ].map((item, i) => (
                      <ListItem key={i}>
                        <ListItemIcon><AttachMoney color="success" fontSize="small" /></ListItemIcon>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
                    Operativas
                  </Typography>
                  <List dense>
                    {[
                      'Smart contracts automatizan pagos',
                      'Distribución automática de rentas',
                      'Registro transparente e inmutable',
                      'Reducción drástica de burocracia',
                      'Cambios en registro de tierras automáticos'
                    ].map((item, i) => (
                      <ListItem key={i}>
                        <ListItemIcon><Speed color="success" fontSize="small" /></ListItemIcon>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
                Proyectos en Europa: Otros Casos
              </Typography>
              <Grid container spacing={2}>
                {[
                  {
                    title: 'Estacionamientos Tokenizados',
                    desc: 'Proyectos en Europa implementan NFTs para reserva y licenciamiento de estacionamientos. La transferencia fácil hace el activo líquido, especialmente para largo plazo.',
                    location: 'Europa',
                    icon: <LocalAtm />
                  },
                  {
                    title: 'Kunang Kunang Tent Resort',
                    desc: 'Resort en Indonesia respaldado por tokenización fraccionaria. Completamente operacional, entregó primeros dividendos a holders de NFT.',
                    location: 'Indonesia',
                    icon: <Home />
                  },
                  {
                    title: 'Ubitquity',
                    desc: 'Registro de títulos de propiedad en blockchain usando Hyperledger. Adoptado por gobiernos municipales en Brasil y Estados Unidos.',
                    location: 'Global',
                    icon: <VerifiedUser />
                  }
                ].map((proyecto, i) => (
                  <Grid item xs={12} md={4} key={i}>
                    <Card variant="outlined" sx={{ height: '100%', p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ color: 'primary.main', mr: 1, fontSize: 35 }}>{proyecto.icon}</Box>
                        <Box>
                          <Typography variant="h6" fontWeight="bold">{proyecto.title}</Typography>
                          <Chip label={proyecto.location} size="small" color="primary" variant="outlined" />
                        </Box>
                      </Box>
                      <Typography variant="body2">{proyecto.desc}</Typography>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Alert severity="success" icon={<EmojiObjects />}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Tendencias Futuras (2025-2030)
            </Typography>
            <Typography variant="body1" paragraph>
              Según reportes de la industria, la tokenización de activos del mundo real será una 
              <strong> oportunidad de negocio de $16 TRILLONES para 2030</strong>.
            </Typography>
            <Grid container spacing={2}>
              {[
                'Mayor adopción institucional',
                'Claridad regulatoria mejorada',
                'Integración con sistemas de registro tradicionales',
                'Mayor interoperabilidad entre blockchains',
                'Herramientas más amigables para no-cripto usuarios'
              ].map((trend, i) => (
                <Grid item xs={12} md={6} key={i}>
                  <Typography variant="body2">✓ {trend}</Typography>
                </Grid>
              ))}
            </Grid>
          </Alert>
        </Grid>
      </Grid>
    </Box>
  );

  // SECCIÓN 4: TECNOLOGÍA
  const Tecnologia = () => (
    <Box>
      <Typography variant="h3" gutterBottom color="primary" sx={{ mb: 4, fontWeight: 'bold' }}>
        Hyperledger vs Blockchain Pública
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Alert severity="info" icon={<Info />}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              ¿Qué es Hyperledger?
            </Typography>
            <Typography variant="body1">
              Proyecto open-source de la <strong>Linux Foundation</strong> que ofrece múltiples frameworks de blockchain empresarial.
            </Typography>
          </Alert>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
                Frameworks de Hyperledger
              </Typography>
              <Grid container spacing={2}>
                {[
                  { name: 'Hyperledger Fabric', desc: 'El más usado para casos empresariales', icon: <Code />, color: '#2196f3' },
                  { name: 'Hyperledger Besu', desc: 'Compatible con Ethereum', icon: <CloudQueue />, color: '#4caf50' },
                  { name: 'Hyperledger Iroha', desc: 'Diseñado para aplicaciones móviles', icon: <Storage />, color: '#ff9800' },
                  { name: 'Hyperledger Sawtooth', desc: 'Para aplicaciones empresariales modulares', icon: <AccountTree />, color: '#9c27b0' }
                ].map((framework, i) => (
                  <Grid item xs={12} md={6} key={i}>
                    <Card variant="outlined" sx={{ height: '100%', borderLeft: `4px solid ${framework.color}` }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Box sx={{ color: framework.color, mr: 1, fontSize: 30 }}>{framework.icon}</Box>
                          <Typography variant="h6" fontWeight="bold" color={framework.color}>
                            {framework.name}
                          </Typography>
                        </Box>
                        <Typography variant="body2">{framework.desc}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
                Comparación Detallada
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Aspecto</strong></TableCell>
                      <TableCell><strong>Hyperledger (Privada)</strong></TableCell>
                      <TableCell><strong>Ethereum/Polygon (Pública)</strong></TableCell>
                      <TableCell align="center"><strong>Ganador</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      { 
                        aspect: 'Privacidad', 
                        hyper: '✅ Control total sobre quién ve qué datos', 
                        public: '❌ Todas las transacciones públicas',
                        winner: 'hyper'
                      },
                      { 
                        aspect: 'Costos', 
                        hyper: '✅ Sin gas fees, costos predecibles', 
                        public: '❌ Gas fees variables ($5-100+ por transacción)',
                        winner: 'hyper'
                      },
                      { 
                        aspect: 'Velocidad', 
                        hyper: '✅ 3,500+ TPS (miles por segundo)', 
                        public: '⚠️ 15-50 TPS Ethereum, 65K TPS Polygon',
                        winner: 'hyper'
                      },
                      { 
                        aspect: 'Gobernanza', 
                        hyper: '✅ Control total de la cooperativa', 
                        public: '❌ Depende de la red pública',
                        winner: 'hyper'
                      },
                      { 
                        aspect: 'Regulatorio', 
                        hyper: '✅ KYC/AML integrado, más fácil', 
                        public: '⚠️ Complejo, depende de jurisdicción',
                        winner: 'hyper'
                      },
                      { 
                        aspect: 'Energía', 
                        hyper: '✅ Muy eficiente', 
                        public: '⚠️ Depende del mecanismo de consenso',
                        winner: 'hyper'
                      },
                      { 
                        aspect: 'Complejidad', 
                        hyper: '⚠️ Requiere infraestructura propia', 
                        public: '✅ Infraestructura ya existente',
                        winner: 'public'
                      },
                      { 
                        aspect: 'Liquidez', 
                        hyper: '❌ Limitada a mercados privados', 
                        public: '✅ Acceso a mercados globales',
                        winner: 'public'
                      }
                    ].map((row, index) => (
                      <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' } }}>
                        <TableCell><strong>{row.aspect}</strong></TableCell>
                        <TableCell sx={{ bgcolor: row.winner === 'hyper' ? '#e8f5e9' : 'inherit' }}>
                          {row.hyper}
                        </TableCell>
                        <TableCell sx={{ bgcolor: row.winner === 'public' ? '#e8f5e9' : 'inherit' }}>
                          {row.public}
                        </TableCell>
                        <TableCell align="center">
                          {row.winner === 'hyper' ? (
                            <Chip label="Hyperledger" color="success" size="small" />
                          ) : (
                            <Chip label="Pública" color="primary" size="small" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={3} sx={{ bgcolor: '#e3f2fd' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
                Por Qué Hyperledger es Ideal para Cooperativas
              </Typography>
              <Grid container spacing={2}>
                {[
                  {
                    title: '1. Privacidad y Confidencialidad',
                    icon: <Lock />,
                    items: [
                      'Channels (canales): Redes privadas dentro de blockchain',
                      'Private Data Collections: Datos compartidos selectivamente',
                      'Control granular sobre quién ve cada transacción',
                      'Miembros NO quieren datos financieros públicos'
                    ],
                    example: 'Canal 1: Miembros + Admin | Canal 2: Cooperativa + Reguladores | Canal 3: Cooperativa + Proveedores'
                  },
                  {
                    title: '2. Costos Predecibles y Bajos',
                    icon: <AttachMoney />,
                    items: [
                      'Sin gas fees: No pagas por cada transacción',
                      'Solo costos de infraestructura (servidores compartidos)',
                      'Escalabilidad sin incremento proporcional de costos',
                      'Cientos de miles de transacciones económicas'
                    ],
                    example: 'Comparación: Ethereum $5-100+ por transacción vs Hyperledger centavos (costo servidor)'
                  },
                  {
                    title: '3. Control Total de Gobernanza',
                    icon: <HowToVote />,
                    items: [
                      'Definir quién participa (miembros verificados)',
                      'Reglas de consenso personalizadas',
                      'Modificar protocolo según necesidades (con voto)',
                      'Integración con sistemas de votación cooperativa'
                    ],
                    example: 'La cooperativa decide todo: desde quién accede hasta cómo funcionan los smart contracts'
                  },
                  {
                    title: '4. Cumplimiento Regulatorio Sencillo',
                    icon: <Gavel />,
                    items: [
                      'KYC/AML integrado: Verificar identidad antes de acceso',
                      'Auditorías: Acceso selectivo a autoridades',
                      'GDPR compliant: Control total sobre datos personales',
                      'No es "cripto especulativa": Evita estigma regulatorio'
                    ],
                    example: 'Puedes cumplir con reguladores sin exponer datos de todos los miembros'
                  },
                  {
                    title: '5. Rendimiento Superior',
                    icon: <Speed />,
                    items: [
                      'Hyperledger Fabric: 3,500+ TPS',
                      'Ethereum: ~15-30 TPS',
                      'Polygon: ~65,000 TPS (menos descentralizado)',
                      'Importante para pagos, cuotas, votaciones diarias'
                    ],
                    example: 'Para una cooperativa procesando operaciones diarias, la velocidad es crítica'
                  },
                  {
                    title: '6. Interoperabilidad con Sistemas Legacy',
                    icon: <Storage />,
                    items: [
                      'Integración con bases de datos SQL, MongoDB',
                      'Conexión con sistemas ERP cooperativos',
                      'APIs bancarias tradicionales',
                      'Sistemas de identidad gubernamentales'
                    ],
                    example: 'No necesitas reemplazar todo, Hyperledger se integra con lo que ya tienes'
                  }
                ].map((ventaja, i) => (
                  <Grid item xs={12} md={6} key={i}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ color: 'primary.main', mr: 1, fontSize: 35 }}>{ventaja.icon}</Box>
                          <Typography variant="h6" fontWeight="bold" color="primary">
                            {ventaja.title}
                          </Typography>
                        </Box>
                        <List dense>
                          {ventaja.items.map((item, j) => (
                            <ListItem key={j}>
                              <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                              <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
                            </ListItem>
                          ))}
                        </List>
                        <Alert severity="info" sx={{ mt: 2 }}>
                          <Typography variant="caption"><strong>Ejemplo:</strong> {ventaja.example}</Typography>
                        </Alert>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={3} sx={{ bgcolor: '#e8f5e9', height: '100%' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom color="success.main" fontWeight="bold">
                <Lock sx={{ mr: 1, verticalAlign: 'middle' }} />
                Usa Hyperledger SI:
              </Typography>
              <List>
                {[
                  'La cooperativa es principalmente local/nacional',
                  'Priorizas privacidad y control',
                  'Necesitas cumplimiento regulatorio estricto',
                  'Tienes recursos para infraestructura',
                  'Quieres costos predecibles',
                  'La liquidez global no es prioridad'
                ].map((item, i) => (
                  <ListItem key={i}>
                    <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
              <Alert severity="success" sx={{ mt: 2 }}>
                <Typography variant="body2" fontWeight="bold">
                  ✅ RECOMENDADO para cooperativas que priorizan control, privacidad y cumplimiento
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={3} sx={{ bgcolor: '#fff3e0', height: '100%' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom sx={{ color: '#ff6f00' }} fontWeight="bold">
                <Public sx={{ mr: 1, verticalAlign: 'middle' }} />
                Usa Blockchain Pública SI:
              </Typography>
              <List>
                {[
                  'Buscas liquidez global inmediata',
                  'Quieres acceso a DeFi y mercados NFT existentes',
                  'No tienes recursos para infraestructura propia',
                  'La transparencia total es prioridad',
                  'Inversores internacionales son clave',
                  'Necesitas ecosistema DeFi establecido'
                ].map((item, i) => (
                  <ListItem key={i}>
                    <ListItemIcon><CheckCircle sx={{ color: '#ff6f00' }} /></ListItemIcon>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2" fontWeight="bold">
                  ⚠️ Considerar si la transparencia total y costos variables son aceptables
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={3} sx={{ bgcolor: '#f3e5f5' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom color="secondary" fontWeight="bold">
                Modelo Híbrido: Lo Mejor de Ambos Mundos
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
                    Hyperledger Fabric (Privada) para:
                  </Typography>
                  <List dense>
                    {[
                      'Gestión interna de la cooperativa',
                      'Datos personales de miembros',
                      'Transacciones diarias (pagos, cuotas, votaciones)',
                      'Documentos legales y contratos',
                      'Administración operativa'
                    ].map((item, i) => (
                      <ListItem key={i}>
                        <ListItemIcon><Lock color="primary" fontSize="small" /></ListItemIcon>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom fontWeight="bold" color="secondary">
                    Blockchain Pública (Ethereum/Polygon) para:
                  </Typography>
                  <List dense>
                    {[
                      'Anclar hashes de estados importantes (proof of existence)',
                      'Mercado secundario abierto (liquidez global)',
                      'Interoperabilidad con DeFi',
                      'Certificaciones públicas verificables',
                      'Trading internacional de tokens'
                    ].map((item, i) => (
                      <ListItem key={i}>
                        <ListItemIcon><Public color="secondary" fontSize="small" /></ListItemIcon>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              </Grid>
              <Alert severity="success" sx={{ mt: 2 }} icon={<EmojiObjects />}>
                <Typography variant="body1">
                  <strong>Esto permite:</strong> Privacidad (datos en Hyperledger) + Verificabilidad 
                  (hashes públicos en Ethereum) + Flexibilidad (mercado secundario opcional)
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
                Casos de Uso Reales con Hyperledger
              </Typography>
              <Grid container spacing={2}>
                {[
                  {
                    name: 'Propy',
                    desc: 'Usa Hyperledger Fabric para gestionar documentos legales privados y coordinar entre compradores, vendedores y notarios.',
                    icon: <Home />
                  },
                  {
                    name: 'Velox.RE',
                    desc: 'Plataforma de tokenización inmobiliaria UK usando Hyperledger, enfocada en cumplimiento regulatorio británico.',
                    icon: <Gavel />
                  },
                  {
                    name: 'Harbor',
                    desc: 'Tokenización de valores inmobiliarios con Hyperledger para gestión privada de inversores y cumplimiento SEC.',
                    icon: <Security />
                  },
                  {
                    name: 'Ubitquity',
                    desc: 'Registro de títulos de propiedad usando Hyperledger. Adoptado por gobiernos en Brasil y USA.',
                    icon: <VerifiedUser />
                  }
                ].map((caso, i) => (
                  <Grid item xs={12} md={6} key={i}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Box sx={{ color: 'primary.main', mr: 1, fontSize: 30 }}>{caso.icon}</Box>
                          <Typography variant="h6" fontWeight="bold">{caso.name}</Typography>
                        </Box>
                        <Typography variant="body2">{caso.desc}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );